import React, { FC, useEffect, useMemo, useState } from 'react';
import './ReleaseNotes.scss';
import clsx from 'clsx';
import Button from '@srm/ui/button';
import { MdCampaign, MdClose } from 'react-icons/md';
import { ReleaseNotesService } from '@services/ReleaseNotesService';
import DOMPurify from 'dompurify';
import { IReleaseShortNotesDto } from '@models/releaseNotes/IReleaseShortNotesDto';
import { useTypedSelector } from '@hooks/useTypedSelector';

const ReleaseNotes: FC = () => {
    const currentUser = useTypedSelector((state) => state.auth.user);
    const [note, setNote] = useState<IReleaseShortNotesDto>();
    const [hintDismissed, setHintDismissed] = useState(false);
    const [isClosing, setIsClosing] = useState(false);

    const storageKey = note ? `user-${currentUser.username}.releaseNotes.${note.key}` : '';

    useEffect(() => {
        ReleaseNotesService.fetchLastItem().then(function (response) {
            if (response?.data?.key) {
                const key = `user-${currentUser.username}.releaseNotes.${response.data.key}`;
                try {
                    const raw = localStorage.getItem(key);
                    if (raw !== null && JSON.parse(raw) === true) {
                        setHintDismissed(true);
                    }
                } catch {
                    // ignore parse errors
                }
                setNote(response.data);
            }
        }).catch(() => {
            // плашка не критична: при ошибке загрузки просто не показываем её
        });
    }, []);

    const replaceLinkText = (data: IReleaseShortNotesDto): string => {
        const url = `/releasenotes?key=${data.key}`;
        const short = data.short ?? '';
        // %link% может стоять «голым» токеном или внутри href ссылки, вставленной
        // через редактор. В href подставляем сам URL; «голый» токен оборачиваем в
        // кликабельную ссылку, иначе он остаётся простым (некликабельным) текстом.
        const inHref = /href\s*=\s*["'][^"']*%link%/i.test(short);
        return inHref
            ? short.replaceAll('%link%', url)
            : short.replaceAll('%link%', `<a href="${url}">Подробнее</a>`);
    };

    const text = useMemo(() => {
        return note ? replaceLinkText(note) : null;
    }, [note]);

    const handleClose = () => {
        setIsClosing(true);
        if (storageKey) {
            localStorage.setItem(storageKey, JSON.stringify(true));
        }
    };

    // Удаляем плашку из DOM только после того, как анимация схлопывания
    // (grid-template-rows: 1fr → 0fr) полностью завершилась.
    const handleTransitionEnd = (e: React.TransitionEvent<HTMLDivElement>) => {
        if (isClosing && e.propertyName === 'grid-template-rows') {
            setHintDismissed(true);
        }
    };

    return note && !hintDismissed && text ? (
        <div
            className={clsx('rf-releasenotes', isClosing && 'rf-releasenotes--closing')}
            onTransitionEnd={handleTransitionEnd}
        >
            <div className="rf-releasenotes-inner">
                <div className="rf-releasenotes-bar" id="releaseNotes">
                    <div className="rf-releasenotes-icon" aria-hidden="true">
                        <MdCampaign size="20" />
                    </div>
                    <div className="rf-releasenotes-body">
                        <div
                            className="rf-releasenotes-content"
                            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(text) }}
                            id="rf-releasenotes-content"
                        />
                    </div>
                    <div className="rf-releasenotes-close" id="rf-releasenotes-close">
                        <Button
                            variant="text"
                            tone="neutral"
                            size="xs"
                            aria-label="Закрыть окно"
                            onClick={handleClose}
                            startAdornment={<MdClose size="20" />}
                        />
                    </div>
                </div>
            </div>
        </div>
    ) : (
        <></>
    );
};

export default ReleaseNotes;
